/**
 * Dell PowerEdge XE9680 Server Profile
 *
 * Source: research/server-specs.md
 * - 6U form factor
 * - 8-GPU HGX platform (H100/H200/B200)
 * - Dual Intel Xeon Scalable (up to 64 cores each)
 * - Up to 4TB DDR5
 * - 6x 2800W PSU
 * - Liquid cooling capable
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Dell PowerEdge XE9680 with HGX H100 80GB
 */
export const DELL_XE9680_H100: ServerProfile = {
  id: 'dell-xe9680-h100',
  name: 'Dell PowerEdge XE9680 (HGX H100)',
  role: 'compute',
  vendor: 'Dell',
  model: 'PowerEdge XE9680',

  cpu: {
    sockets: 2,
    coresPerSocket: 64,
    model: 'Intel Xeon Platinum 8480+ (5th Gen)',
    tdpPerSocketWatts: 350,
    baseClockGHz: 2.0,
    turboClockGHz: 3.8,
  },

  memory: {
    totalGB: 2048,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 64,
  },

  gpu: {
    platformId: 'hgx-h100-80gb',
    gpusPerNode: 8,
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 8,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 2,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 16800,
    typicalPowerWatts: 8000,
    idlePowerWatts: 500,
    redundancy: 'N+1',
    psuCount: 6,
    psuCapacityWatts: 2800,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC or 240V DC',
  },

  dimensions: {
    rackUnits: 6,
    widthMm: 482.6,
    depthMm: 900,
    heightMm: 267,
    weightKg: 75,
    maxAltitudeMeters: 3048,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 10, max: 80 },
    maxBtuPerHour: 27296, // 8000W * 3.412
    coolingType: 'liquid',
    noiseLevelDbA: 75,
  },

  metadata: {
    productUrl: 'https://www.dell.com/en-us/shop/ipovw/poweredge-xe9680',
    notes: 'Flagship 8-GPU HGX platform with liquid-assisted air cooling or direct liquid cooling',
    validated: true,
  },
} as const;

/**
 * Dell PowerEdge XE9680 with HGX B200
 */
export const DELL_XE9680_B200: ServerProfile = {
  id: 'dell-xe9680-b200',
  name: 'Dell PowerEdge XE9680 (HGX B200)',
  role: 'compute',
  vendor: 'Dell',
  model: 'PowerEdge XE9680',

  cpu: {
    sockets: 2,
    coresPerSocket: 64,
    model: 'Intel Xeon Platinum 8580 (5th Gen)',
    tdpPerSocketWatts: 350,
    baseClockGHz: 2.0,
    turboClockGHz: 4.0,
  },

  memory: {
    totalGB: 2048,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 64,
  },

  gpu: {
    platformId: 'hgx-b200',
    gpusPerNode: 8,
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 8,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 2,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 16800,
    typicalPowerWatts: 10000,
    idlePowerWatts: 500,
    redundancy: 'N+1',
    psuCount: 6,
    psuCapacityWatts: 2800,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC or 240V DC',
  },

  dimensions: {
    rackUnits: 6,
    widthMm: 482.6,
    depthMm: 900,
    heightMm: 267,
    weightKg: 80,
    maxAltitudeMeters: 3048,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 10, max: 80 },
    maxBtuPerHour: 34120, // 10000W * 3.412
    coolingType: 'liquid',
    noiseLevelDbA: 78,
  },

  metadata: {
    productUrl: 'https://www.dell.com/en-us/shop/ipovw/poweredge-xe9680',
    notes: 'HGX B200 configuration requires direct liquid cooling for 1000W GPUs',
    validated: true,
  },
} as const;
